import { useEffect, useState, type ReactNode } from 'react';
import { useParams, Link } from 'react-router-dom';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { Button } from '../../../../shared/components/index.js';
import { JobAssignmentSummary } from './JobAssignmentSummary.js';

/** T063 Rejection Reason Master row (GET /master/rejection-reasons?status=ACTIVE). */
interface Reason {
  readonly reasonId: string;
  readonly reasonCode: string;
  readonly reasonDescription: string;
  readonly requiresRemarks: boolean;
  readonly sortOrder: number;
}

interface RejectResult {
  readonly assignmentId: string;
  readonly status: string;
  readonly respondedAt: string;
}

/** §10 Job Rejection — reason bound to Rejection Reason Master (LAW-6), remarks mandatory (§32). */
export function VendorJobRejectPage(): ReactNode {
  const { assignmentId } = useParams<{ assignmentId: string }>();
  const { token } = useAuth();
  const { t } = useTranslation();
  const [reasons, setReasons] = useState<readonly Reason[] | null>(null);
  const [reasonCode, setReasonCode] = useState('');
  const [remarks, setRemarks] = useState('');
  const [result, setResult] = useState<RejectResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        // Scoped to M08. RejectionReasonService returns the module's reasons
        // plus any global ones, so without this a vendor refusing a job was
        // offered M10 quality and M14 billing reasons — "Tax error", "Execution
        // quality below acceptable standard" — which describe work already
        // done, not a reason to decline it.
        const r = await apiRequest<Reason[]>('/master/rejection-reasons?status=ACTIVE&module=M08', { token });
        if (alive) setReasons(Array.isArray(r) ? r : []);
      } catch (e) {
        if (alive) setError((e as Error).message);
      }
    })();
    return () => {
      alive = false;
    };
  }, [token]);

  async function reject() {
    if (!reasonCode) {
      setError(t('vops.jobReject.reasonRequired'));
      return;
    }
    if (!remarks.trim()) {
      setError(t('vops.jobReject.remarksRequired'));
      return;
    }
    setError(null);
    setBusy(true);
    try {
      const r = await apiRequest<RejectResult>(`/v1/vendor/assignments/${assignmentId}/reject`, {
        method: 'POST',
        token,
        body: { reasonCode, remarks: remarks.trim() },
      });
      setResult(r);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <main aria-label="vendor-job-reject">
      <h1>{t('vops.jobReject.title')}</h1>
      <p><Link to={`/vendor-ops/jobs/${assignmentId}`} aria-label="back-to-job">{t('vops.common.backToJob')}</Link></p>

      <JobAssignmentSummary assignmentId={assignmentId} />

      {error ? <p role="alert">{error}</p> : null}
      {result ? (
        <p role="status" aria-label="reject-result">{t('vops.jobReject.result', { status: result.status })}</p>
      ) : null}

      <label htmlFor="reject-reason">{t('vops.common.reason')}</label>
      <select
        id="reject-reason"
        aria-label="reject-reason"
        value={reasonCode}
        onChange={(e) => setReasonCode(e.target.value)}
        disabled={busy || result !== null || reasons === null}
      >
        <option value="">{t('vops.jobReject.selectReason')}</option>
        {(reasons ?? []).map((r) => (
          <option key={r.reasonId} value={r.reasonCode}>{r.reasonDescription}</option>
        ))}
      </select>

      <label htmlFor="reject-remarks">{t('vops.jobReject.remarks')}</label>
      <textarea
        id="reject-remarks"
        aria-label="reject-remarks"
        value={remarks}
        onChange={(e) => setRemarks(e.target.value)}
        disabled={busy || result !== null}
      />

      <Button
        type="button"
        aria-label="reject-btn"
        onClick={reject}
        disabled={busy || result !== null}
        variant="danger"
      >
        {t('vops.jobReject.rejectBtn')}
      </Button>
    </main>
  );
}
